// This is the root file. Its also called Admin.jsx

import React from "react";
import { Routes, Route, Navigate, useLocation, Outlet, matchPath } from "react-router-dom";

// implemented route definitions
import routes from "routes.js";

// import hooks and utils
import { usePageMeta } from "@/hooks/usePageMeta";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import useSaveBarStore from "@/stores/useSaveBarStore";

// Reusable components
import TopBar from "components/topbar";
import Sidebar from "components/sidebar";
import Footer from "components/footer/Footer";
import ContextualSaveBar from "@/components/ContextualSaveBar/ContextualSaveBar";
import GlobalImageManager from "@/components/ImageManager/GlobalImageManager";
import { Toaster } from "sonner";

export default function Admin(props) {
  const { ...rest } = props;
  const location = useLocation();

  // Page title / breadcrumb metadata + contextual save bar
  const { mode, pageTitle } = usePageMeta();

  // Tab title: "<Store name> • <Nav destination> • Stocko"
  useDocumentTitle();
  const { isVisible, onSave, onDiscard } = useSaveBarStore();

  // Track the sidebar state
  const [open, setOpen] = React.useState(true);

  // Used to display the current page name in the navbar crumbs
  const [currentRoute, setCurrentRoute] = React.useState("Main Dashboard");

  // Collapse sidebar on small screens
  React.useEffect(() => {
    const handleResize = () => {
      setOpen(window.innerWidth >= 1200);
    };
  
    // Run once on mount to set correct sidebar state
    handleResize();
  
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Detect and update active route name when location changes
  React.useEffect(() => {
    getActiveRoute(routes);
  }, [location.pathname]);

  // Determines which route is currently active to update navbar title
  const getActiveRoute = (routes) => {
    let activeRoute = "Main Dashboard";

    for (let i = 0; i < routes.length; i++) {
      if (window.location.href.indexOf(routes[i].layout + "/" + routes[i].path) !== -1) {
        setCurrentRoute(routes[i].name);
      }
    };

    return activeRoute;
  };

  document.documentElement.dir = "ltr";
  
  return (
    <div className="flex flex-col w-full h-screen overflow-hidden">
      {/* Global Wix-style top bar */}
      <div className="w-full shrink-0 z-50">
        <TopBar onToggleSidebar={() => setOpen((o) => !o)} />
      </div>

      <div className="flex w-full flex-1 overflow-hidden">
      {/* Mobile backdrop: closes the drawer when tapped */}
      {open && (
        <div
          className="fixed inset-x-0 bottom-0 top-12 z-40 bg-black/40 xl:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* sidebar that contains the routes to main pages */}
      <div
        onClick={(e) => {
          // On mobile, close the drawer after tapping a nav link
          if (e.target.closest("a") && window.innerWidth < 1200) setOpen(false);
        }}
        style={{ transform: open ? "translateX(0)" : "translateX(-100%)" }}
        className="w-[230px] fixed top-12 bottom-0 overflow-y-auto xl:relative xl:top-auto xl:bottom-auto xl:h-auto border-r border-[#232a36] bg-[#141922] transition-transform duration-200 ease-linear text-white z-50 xl:z-0"
      >
        <Sidebar open={open} onClose={() => setOpen(false)} />
      </div>

      {/* Main Content */}
      <div className="h-full flex-1 flex-col overflow-y-auto bg-[#f2f3f5] dark:!bg-navy-900">
        {/* Contextual save bar (shown while editing) */}
        {isVisible && (
          <div className="sticky top-0 z-30 w-full border-b border-[#e4e4e4] bg-white px-2 py-2 flex justify-center">
            <ContextualSaveBar onSave={onSave} onDiscard={onDiscard} />
          </div>
        )}

        <div className="h-fit flex flex-col items-center mx-2 sm:mx-4 xl:mx-[3em] transition-all md:pr-2">
          {/* Establish content of the page */}
          <div className="pt-6 mx-auto mb-auto w-full p-2 md:pr-2">
            {/* In-page title for top-level pages (Home provides its own hero) */}
            {mode === "title" && pageTitle !== "Home" && (
              <h1 className="mb-4 text-[28px] font-bold text-[#111] dark:text-white">
                {pageTitle}
              </h1>
            )}
            <Outlet />
          </div>
        </div>

        {/* Sonner toqast messages declaration */}
        <Toaster 
          position="bottom-right"
          richColors
          closeButton
          expand
        />

        {/* The global image manager */}
        <GlobalImageManager />
      </div>
      </div>
    </div>
  );
}
